import { Col, Form, Input, Row, Spin } from "antd";
import "antd/dist/antd.min.css";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { postRequest } from "../../axios";
import ButtonField from "../../common/htmlComponents/ButtonField";
import {
  CheckCircleTwoTone,
  EyeInvisibleOutlined,
  EyeTwoTone,
} from "@ant-design/icons";
import { SuccessNotificationMsg } from "../../utils/Notifications";

const DriverChangePassword = ({
  handleCancel,
  setReload,
  changePasswordDriverId,
  modalKey,
  setStateM,
}) => {
  const { t } = useTranslation();
  const formRef = useRef();

  const initialState = {
    confirm_password: null,
    new_password: null,
    email: changePasswordDriverId,
  };

  const [state, setState] = useState(initialState);
  const [btnLoading, setBtnLoading] = useState(false);
  const [apiLoading, setApiLoading] = useState(false);

  // Password condition states
  const [hasUppercase, setHasUppercase] = useState(false);
  const [hasLowercase, setHasLowercase] = useState(false);
  const [hasSpecialChar, setHasSpecialChar] = useState(false);
  const [hasNumber, setHasNumber] = useState(false);
  const [hasMinLength, setHasMinLength] = useState(false);
  const [hasStartedTyping, setHasStartedTyping] = useState(false); // Track when the user starts typing
  const validatePassword = (password) => {
    setHasUppercase(/[A-Z]/.test(password));
    setHasLowercase(/[a-z]/.test(password));
    setHasSpecialChar(/[!@#$%^&*(),.?":{}|<>]/.test(password));
    setHasNumber(/\d/.test(password));
    setHasMinLength(password.length >= 8);
  };
  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      confirm_password: null,
      new_password: null,
      email: changePasswordDriverId,
    }));
  }, [modalKey]);
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        backToList();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  });
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setState({ ...state, [name]: value });

    if (name === "new_password") {
      setHasStartedTyping(true); // Set this to true when user starts typing
      validatePassword(value);
    }
  };

  const validatePasswordsMatch = (_, value) => {
    if (value && value !== state.new_password) {
      return Promise.reject(new Error(t("Passwords do not match")));
    }
    return Promise.resolve();
  };

  const handleSubmit = async () => {
    try {
      setBtnLoading(true);
      let response;

      response = await postRequest("change-password-with-email/", state);
      if (response.data.success) {
        setState((prevState) => ({
          ...prevState,
          confirm_password: null,
          new_password: null,
          email: null,
        }));

        formRef.current.setFieldsValue(initialState);
        setBtnLoading(false);
        backToList();
        setReload((reload) => !reload);
      }
      SuccessNotificationMsg(t("Password Changed Successfully"));
    } catch (error) {
      setBtnLoading(false);
    }
  };
  const backToList = () => {
    // setState(initialState);
    setState((prevState) => ({
      ...prevState,
      confirm_password: null,
      new_password: null,
      email: null,
    }));
    formRef.current.setFieldsValue(initialState);
    setHasMinLength(false);
    setHasNumber(false);
    setHasSpecialChar(false);
    setHasLowercase(false);
    setHasUppercase(false);
    handleCancel();
    setHasStartedTyping(false);
  };
  return (
    <Spin spinning={apiLoading}>
      <Form
        className="ModalForm"
        onFinish={handleSubmit}
        autoComplete="off"
        ref={formRef}
        initialValues={state}
      >
        <Row gutter={16}>
          <Col md={24} className="TextFeild">
            <Form.Item
              name="new_password"
              label={t("New Password")}
              rules={[
                {
                  required: true,
                  message: t("Please enter a strong password"),
                },
                {
                  pattern: /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/,
                  message: t(
                    "Password must be at least 8 characters, include an uppercase, lowercase letter, and a number"
                  ),
                },
              ]}
            >
              <Input.Password
                name="new_password"
                placeholder={t("Please enter your password")}
                iconRender={(visible) =>
                  visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                }
                onChange={handleInputChange}
              />
            </Form.Item>
          </Col>

          <Col md={24} className="TextFeild">
            <Form.Item
              name="confirm_password"
              label={t("Confirm Password")}
              dependencies={["new_password"]}
              rules={[
                {
                  required: true,
                  message: t(""),
                },
                {
                  validator: validatePasswordsMatch,
                },
              ]}
            >
              <Input.Password
                name="confirm_password"
                placeholder={t("Please confirm your password")}
                iconRender={(visible) =>
                  visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                }
                onChange={handleInputChange}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Password Conditions */}
        <ul style={{ listStyle: "none", padding: 0 }}>
          <li
            style={{
              color: !hasStartedTyping
                ? "#000"
                : hasMinLength
                ? "#5cb85c"
                : "#d9534f",
              marginBottom: "5px",
              fontSize: "14px",
            }}
          >
            {hasMinLength ? <CheckCircleTwoTone twoToneColor="#52c41a" /> : "-"}{" "}
            {t("Minimum 8 characters.")}
          </li>
          <li
            style={{
              color: !hasStartedTyping
                ? "#000"
                : hasUppercase
                ? "#5cb85c"
                : "#d9534f",
              marginBottom: "5px",
              fontSize: "14px",
            }}
          >
            {hasUppercase ? <CheckCircleTwoTone twoToneColor="#52c41a" /> : "-"}{" "}
            {t("At least one uppercase letter (A-Z).")}
          </li>
          <li
            style={{
              color: !hasStartedTyping
                ? "#000"
                : hasLowercase
                ? "#5cb85c"
                : "#d9534f",
              marginBottom: "5px",
              fontSize: "14px",
            }}
          >
            {hasLowercase ? <CheckCircleTwoTone twoToneColor="#52c41a" /> : "-"}{" "}
            {t("At least one lowercase letter (a-z).")}
          </li>
          <li
            style={{
              color: !hasStartedTyping
                ? "#000"
                : hasNumber
                ? "#5cb85c"
                : "#d9534f",
              marginBottom: "5px",
              fontSize: "14px",
            }}
          >
            {hasNumber ? <CheckCircleTwoTone twoToneColor="#52c41a" /> : "-"}{" "}
            {t("At least one digit (0-9).")}
          </li>
          <li
            style={{
              color: !hasStartedTyping
                ? "#000"
                : hasSpecialChar
                ? "#5cb85c"
                : "#d9534f",
              marginBottom: "5px",
              fontSize: "14px",
            }}
          >
            {hasSpecialChar ? (
              <CheckCircleTwoTone twoToneColor="#52c41a" />
            ) : (
              "-"
            )}{" "}
            {t("At least one special character (@$!%*?&).")}
          </li>
        </ul>

        <div className="ModalPopupFooter">
          <ButtonField
            className="CancelGradiantBtn"
            type="primary"
            htmlType="button"
            label={t("cancel")}
            onClick={backToList}
          />
          <ButtonField
            className="SaveGradiantBtn"
            type="primary"
            htmlType="submit"
            label={t("Save")}
            loading={btnLoading}
          />
        </div>
      </Form>
    </Spin>
  );
};

DriverChangePassword.propTypes = {
  handleCancel: PropTypes.func.isRequired,
  modalKey: PropTypes.bool,
  setStateM: PropTypes.func.isRequired,
  setReload: PropTypes.bool,
  changePasswordDriverId: PropTypes.func.isRequired,
};

export default DriverChangePassword;
